import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Typography, Spin, message, Empty, Tag, Avatar, theme, Button,
} from 'antd';
import { UserOutlined, LineChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ventaService } from '../../services/api';

const { Title, Text } = Typography;

const AnimalSociedadCard = ({ animal, onVerMovimientos }) => {
  const { token } = theme.useToken();

  const imagenPrincipal =
    animal.imagenes?.find((i) => i.es_principal) ||
    animal.imagenes?.[0];

  const pctVendido  = animal.pct_total_vendido || 0;
  const pctProductor = parseFloat((100 - pctVendido).toFixed(1));

  // Productor primero, luego copropietarios ordenados por % desc
  const propietarios = [];
  if (pctProductor > 0) {
    propietarios.push({ nombre: 'Productor', pct: pctProductor, esProductor: true });
  }
  (animal.copropietarios || [])
    .slice()
    .sort((a, b) => b.porcentaje_venta - a.porcentaje_venta)
    .forEach((v) => {
      propietarios.push({
        nombre: v.usuario?.nom_usuario || `ID ${v.id_usuario}`,
        pct: parseFloat(v.porcentaje_venta),
        esProductor: false,
      });
    });

  return (
    <Card
      style={{ marginBottom: 16, borderRadius: 8 }}
      styles={{ body: { padding: 16, paddingBottom: 0 } }}
      actions={[
        <Button
          key="movimientos"
          type="link"
          icon={<LineChartOutlined />}
          onClick={() => onVerMovimientos(animal.id_animal)}
        >
          Ver movimientos
        </Button>,
      ]}
    >
      <Row gutter={16} align="top" wrap={false}>
        {/* Imagen */}
        <Col flex="none">
          {imagenPrincipal ? (
            <img
              src={imagenPrincipal.url_thumb || imagenPrincipal.url_imagen}
              alt={animal.nom_animal}
              style={{
                width: 110,
                height: 110,
                objectFit: 'cover',
                borderRadius: 8,
                display: 'block',
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <Avatar
              size={110}
              icon={<UserOutlined />}
              style={{ background: token.colorFillSecondary }}
            />
          )}
        </Col>

        {/* Info */}
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Text strong style={{ fontSize: 15, display: 'block' }}>
            {animal.nom_animal}
          </Text>
          <div style={{ marginTop: 4, marginBottom: 10 }}>
            {animal.nom_raza && <Tag color="blue">{animal.nom_raza}</Tag>}
            {animal.rp_animal && <Tag>RP: {animal.rp_animal}</Tag>}
          </div>

          {/* Propietarios — cada fila: nombre izquierda, % derecha */}
          <div
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            {propietarios.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 10px',
                  background: i % 2 === 0 ? token.colorFillQuaternary : 'transparent',
                  borderTop: i > 0 ? `1px solid ${token.colorBorderSecondary}` : 'none',
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: p.esProductor ? 600 : 400 }}>
                  {p.nombre}
                </Text>
                <Tag
                  color={p.esProductor ? 'green' : 'geekblue'}
                  style={{ margin: 0, fontWeight: 600, minWidth: 48, textAlign: 'center' }}
                >
                  {p.pct}%
                </Tag>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </Card>
  );
};

const Movimientos = () => {
  const navigate = useNavigate();
  const [animales, setAnimales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const res = await ventaService.getSociedad();
        setAnimales(res.data || []);
      } catch {
        message.error('Error al cargar animales en sociedad');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 8 }}>Movimientos</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Animales en sociedad — porcentaje de propiedad compartida
      </Text>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && animales.length === 0 && (
        <Empty description="No hay animales en sociedad registrados" />
      )}

      {!loading && animales.map((animal) => (
        <AnimalSociedadCard
          key={animal.id_animal}
          animal={animal}
          onVerMovimientos={(id) => navigate(`/ventas/movimientos/${id}`)}
        />
      ))}
    </div>
  );
};

export default Movimientos;
